import Box from '@mui/material/Box';
import Shimmer from './Shimmer';

// Skeleton for the Membership pricing grid. Mirrors a plan card:
//   tier eyebrow · big centered price · tagline · divider · 5 perk rows
//   (circle check + text) · full-width CTA. Middle card gets the amber
//   "Most Popular" pill so it visually matches the featured plan slot.
//
// Rendered on the forest-green page bg, so every shimmer uses a
// translucent white tint so it reads against the dark background.

const PlanCardShimmer = ({ featured = false, perks = 5 }) => (
  <Box
    sx={{
      bgcolor: featured ? 'rgba(61,122,82,0.15)' : 'rgba(255,255,255,0.03)',
      border: '1.5px solid',
      borderColor: featured ? 'rgba(106,174,128,0.5)' : 'rgba(255,255,255,0.1)',
      borderRadius: '20px',
      p: { xs: 3, md: 3.5 },
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {featured && (
      <Shimmer
        width={120}
        height={22}
        rounded={100}
        sx={{
          position: 'absolute',
          top: -11,
          left: '50%',
          transform: 'translateX(-50%)',
          bgcolor: 'rgba(201,125,8,0.45)',
        }}
      />
    )}

    {/* Tier eyebrow */}
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
      <Box sx={{ width: 18, height: '1.5px', bgcolor: 'var(--sage-l)', flexShrink: 0 }} />
      <Shimmer width={100} height={10} rounded={4} sx={{ bgcolor: 'rgba(200,237,216,0.25)' }} />
    </Box>

    {/* Price */}
    <Shimmer width={140} height={44} rounded={6} sx={{ mx: 'auto', mb: 1, bgcolor: 'rgba(255,255,255,0.14)' }} />
    <Shimmer width={120} height={10} rounded={4} sx={{ mx: 'auto', mb: 3, bgcolor: 'rgba(255,255,255,0.10)' }} />

    {/* Divider */}
    <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.08)', mb: 2.25 }} />

    {/* Perks */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1, mb: 2.5 }}>
      {Array.from({ length: perks }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Shimmer width={18} height={18} circular sx={{ bgcolor: 'rgba(106,174,128,0.25)' }} />
          <Shimmer
            width={`${55 + (i * 11) % 40}%`}
            height={11}
            rounded={4}
            sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
          />
        </Box>
      ))}
    </Box>

    {/* CTA */}
    <Shimmer
      width="100%"
      height={40}
      rounded={100}
      sx={{ bgcolor: featured ? 'rgba(201,125,8,0.45)' : 'rgba(106,174,128,0.2)' }}
    />
  </Box>
);

const MembershipShimmer = ({ count = 3 }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        md: count === 1 ? '1fr' : count === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
      },
      gap: { xs: 2, md: 2.5 },
      width: '100%',
      alignItems: 'stretch',
      maxWidth: count < 3 ? 800 : 'none',
      mx: 'auto',
    }}
  >
    {Array.from({ length: count }).map((_, i) => (
      <PlanCardShimmer key={i} featured={count >= 3 ? i === 1 : i === 0} perks={5 + (i % 2)} />
    ))}
  </Box>
);

export default MembershipShimmer;
